import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Search, FileText, ShieldCheck } from "lucide-react";

export default function CoaLibrary() {
    const [coas, setCoas] = useState([]);
    const [q, setQ] = useState("");

    useEffect(() => {
        const t = setTimeout(() => {
            api.get(`/coas${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setCoas(r.data));
        }, 200);
        return () => clearTimeout(t);
    }, [q]);

    return (
        <div data-testid="coa-library" className="pt-48 pb-24 bg-[var(--bg)] min-h-screen">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="mb-12">
                    <div className="data-label text-brand mb-3">CERTIFICATE LIBRARY</div>
                    <h1 className="font-serif text-5xl md:text-6xl leading-none">COA Library</h1>
                    <p className="text-secondary max-w-2xl mt-4">
                        Independent laboratory analysis for every Aminoplex batch. Search by product or batch number to verify authenticity.
                    </p>
                </div>

                <div className="card p-4 mb-8 flex items-center">
                    <Search size={16} className="text-muted ml-2 mr-3" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by product (BPC-157) or batch (AP-BPC-…)"
                        data-testid="coa-search"
                        className="flex-1 bg-transparent outline-none font-mono text-sm py-2"
                    />
                </div>

                <div className="card overflow-x-auto">
                    <table className="w-full font-mono text-xs">
                        <thead className="border-b border-[var(--border)]">
                            <tr>
                                {["Product", "Batch", "Purity", "Method", "Lab", "Date", "Action"].map((h) => (
                                    <th key={h} className="text-left px-4 py-4 data-label">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {coas.length === 0 ? (
                                <tr><td colSpan={7} className="p-8 text-center text-secondary">No certificates found.</td></tr>
                            ) : coas.map((c) => (
                                <tr key={c.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-surface)] transition-colors" data-testid={`coa-${c.batch_number}`}>
                                    <td className="px-4 py-4 text-primary">{c.product_name}</td>
                                    <td className="px-4 py-4 text-brand">{c.batch_number}</td>
                                    <td className="px-4 py-4 text-brand">{c.purity}</td>
                                    <td className="px-4 py-4 text-secondary">{c.test_method}</td>
                                    <td className="px-4 py-4 text-secondary">{c.lab_name}</td>
                                    <td className="px-4 py-4 text-secondary">{c.test_date}</td>
                                    <td className="px-4 py-4">
                                        {c.pdf_url ? (
                                            <a href={c.pdf_url} target="_blank" rel="noreferrer" className="link-gold">DOWNLOAD →</a>
                                        ) : (
                                            <span className="data-label text-muted">PDF PENDING</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <InfoCard icon={ShieldCheck} title="Independent Testing" body="Every batch tested by third-party analytical laboratories — not internal QC." />
                    <InfoCard icon={FileText} title="HPLC + Mass Spectrometry" body="Identity confirmed by ESI-MS; purity quantified by RP-HPLC." />
                    <InfoCard icon={Search} title="Batch Traceability" body="Every order ships with the matching COA for its exact lot." />
                </div>
            </div>
        </div>
    );
}

function InfoCard({ icon: Icon, title, body }) {
    return (
        <div className="card p-6">
            <Icon size={20} className="text-brand mb-3" />
            <h3 className="font-serif text-xl mb-2">{title}</h3>
            <p className="text-sm text-secondary leading-relaxed">{body}</p>
        </div>
    );
}
